import styles from "./Product.module.css";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import AddToCartControl from "../UILibrary/AddToCartControl";
import { IProductCardProps } from "@model/Products.model";
import { useNavigate } from "react-router-dom";
import { CartService } from "../../../Logic/CartService";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { UserCartStoreImplementation } from "Data/Cart/CartStoreImplementation";
import { AccountModeModalStoreImplementation } from "Data/AccountModeModal/AccountModeModalStoreImplementation";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";

const ProductCard: React.FC<IProductCardProps> = ({
  id,
  imageUrl,
  name,
  price,
  shortDescription,
  slug,
  stock,
  maxPerUser,
}) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [error, setError] = useState<string | null>("");
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const { authenticationState } = UserAuthenticationStoreImplementation();
  const authToken = authenticationState?.token?.trim() ?? "";
  const isLoggedIn = !!authToken;

  const { openAccountModeModal } = AccountModeModalStoreImplementation();
  const cartService = useMemo(() => new CartService(), []);
  const { itemsICart, addItemToCart } = UserCartStoreImplementation(
    "USD",
    cartService.mapCartStateToICartItems,
    cartService.computeCartSubtotalFromICartItems
  );

  const currentInCart = useMemo(
    () => Number(itemsICart.find((it) => it.id === id)?.qty ?? 0),
    [itemsICart, id]
  );
const onAuthError = useForceLogoutAndRedirect();
  const src = useMemo(() => imageUrl || undefined, [imageUrl]);

  const stockNum = Number.isFinite(Number(stock)) ? Number(stock) : 0;
  const perUserCapRaw = Number(maxPerUser);
  const perUserCap =
    Number.isFinite(perUserCapRaw) && perUserCapRaw > 0
      ? perUserCapRaw
      : Infinity;

  const stockRemaining = Math.max(0, stockNum - currentInCart);
  const limitRemaining = Number.isFinite(perUserCap)
    ? Math.max(0, perUserCap - currentInCart)
    : Infinity;

  const remaining = Math.max(0, Math.min(stockRemaining, limitRemaining));

  const outOfStock = stockNum === 0;
  const noStockLeft = stockRemaining <= 0;
  const userLimitHit =
    !noStockLeft && Number.isFinite(perUserCap) && limitRemaining <= 0;
  const qtyTooHigh = quantity > remaining;

  const message =
    remaining === 0
      ? noStockLeft
        ? "0 left — item is out of stock."
        : userLimitHit
        ? "Limit reached."
        : ""
      : qtyTooHigh
      ? `Only ${remaining} left.`
      : "";

  useEffect(() => setError(message), [message]);
  const handleAddClick = useCallback(
    async (qty: number) => {
      if (remaining === 0 || !!error) return;

      const result = await cartService.handleAddItemToCart({
        isLoggedIn,
        token: authToken,
        productId: id,
        requestQty: qty,
        stockCap: stockNum > 0 ? stockNum : undefined,
        perUserCap: Number.isFinite(perUserCap) ? perUserCap : undefined,
        remaining,
        stockRemaining,
        limitRemaining,
         onAuthError,
        onGuestAdd: (qtyToAdd) => {
          addItemToCart({
            id,
            name,
            price,
            imageUrl,
            shortDescription,
            stock: stockNum,
            maxPerUser: Number.isFinite(perUserCap) ? perUserCap : undefined,
            quantity: qtyToAdd,
          });
        },
        setLoading,
        setApiError: (msg) => setError(msg),
        setSuccessMsg: (msg) => setError(msg ?? ""),
      });

      if (!isLoggedIn && result?.appliedQuantity > 0) {
        openAccountModeModal();
      }
    },

    [
      isLoggedIn,
      authToken,
      id,
      name,
      price,
      imageUrl,
      shortDescription,
      stockNum,
      perUserCap,
      remaining,
      stockRemaining,
      limitRemaining,
      addItemToCart,
      cartService,
      error,
      openAccountModeModal,
    ]
  );

  const target = slug && slug.trim() ? slug.trim() : id;
  const handleSeeMore = () =>
    navigate(`/products/${encodeURIComponent(target)}`);

  return (
    <div className={styles.card} data-testid="product-card">
      <div className={styles.imageWrap}>
        {outOfStock && (
          <span className={styles.badgeOut} data-testid="badge-out-of-stock">
            Out of stock
          </span>
        )}
        {src ? (
          <img
            src={src}
            alt={name}
            className={styles.image}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            width={800}
            height={600}
            sizes="(max-width: 768px) 100vw, 33vw"
            data-testid="product-image"
          />
        ) : (
          <div
            className={styles.imagePlaceholder}
            aria-hidden
            data-testid="product-image-placeholder"
          />
        )}
      </div>

      <div className={styles.details}>
        <h3 className={styles.name} data-testid="product-name">
          {name}
        </h3>

        <div className={styles.infoColumn}>
          <p className={styles.description} data-testid="product-description">
            {shortDescription}
          </p>
        </div>

        <span className={styles.price} data-testid="product-price">
          ${Number(price ?? 0).toFixed(2)}
        </span>

        <div className={styles.actionBar}>
          <div className={styles.addToCartWrap}>
            <AddToCartControl
              quantity={quantity}
              onQuantityChange={setQuantity}
              onError={setError}
              onClick={handleAddClick}
              min={1}
              max={remaining > 0 ? remaining : 1}
              step={1}
              error={error ?? ""}
              loading={loading}
              disabled={remaining === 0}
              buttonClass={styles.fullWidthButton}
              quantityControlClass={styles.centerQuantityControl}
            />
          </div>

          <button
            type="button"
            className={`${styles.seeMore} ${styles.seeMoreButton}`}
            onClick={handleSeeMore}
            data-testid="see-more-button"
          >
            See More
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
